import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tv, Radio, Users, Settings, RefreshCw, Star, Clock, Server, Lock, Sparkles, Film, LogOut } from 'lucide-react';
import toast from 'react-hot-toast';
import { streamFlowApi, bindWsTokenRefresh } from '@lib/streamFlowApi';
import { getWsUrl } from '@lib/apiBase';
import { isTvMode } from '@lib/runtime';
import type { Provider, Seat, FamilyMember, Channel } from '@lib/streamFlowApi';
import { supabase } from '@lib/supabase';
import { useAuth } from '@contexts/AuthContext';
import SeatCard from '@components/streamflow/SeatCard';
import ChannelGrid from '@components/streamflow/ChannelGrid';
import FamilySidebar from '@components/streamflow/FamilySidebar';
import CheckoutModal from '@components/streamflow/CheckoutModal';
import ConnectionBanner from '@components/streamflow/ConnectionBanner';
import InstallPrompt from '@components/streamflow/InstallPrompt';
import BottomNav from '@components/streamflow/BottomNav';
import HouseholdSwitcher from '@components/HouseholdSwitcher';
import BillingBanner from '@components/BillingBanner';
import DevicesPanel from '@components/streamflow/DevicesPanel';

export default function StreamDashboard() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };
  const [providers, setProviders] = useState<Provider[]>([]);
  const [seats, setSeats] = useState<Seat[]>([]);
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [categories, setCategories] = useState<Array<{ name: string; count: number }>>([]);
  const [currentMemberId, setCurrentMemberId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);
  const [checkoutFor, setCheckoutFor] = useState<Provider | null>(null);

  // Full refetch of all dashboard data. Used for the initial load and the
  // explicit "Refresh" button only — realtime seat changes are applied as
  // targeted micro-updates in the WS handler below, so we don't re-pull
  // five endpoints on every claim/release.
  //
  // Each endpoint is settled independently (Promise.allSettled): one failing
  // call must NOT blank the whole dashboard. A rejected/failed piece keeps
  // its current state (or defaults to []) and we surface a single soft
  // warning rather than an error that hides the data that DID load.
  const fetchData = useCallback(async () => {
    const [provRes, seatRes, memRes, chRes, catRes] = await Promise.allSettled([
      streamFlowApi.listProviders(),
      streamFlowApi.listSeats(),
      streamFlowApi.listMembers(),
      streamFlowApi.listChannels({ limit: 100 }),
      streamFlowApi.listCategories(),
    ]);

    let anyFailed = false;

    if (provRes.status === 'fulfilled' && provRes.value.success) {
      setProviders(provRes.value.data || []);
    } else {
      anyFailed = true;
    }

    if (seatRes.status === 'fulfilled' && seatRes.value.success) {
      setSeats(seatRes.value.data || []);
    } else {
      anyFailed = true;
    }

    if (memRes.status === 'fulfilled' && memRes.value.success) {
      const mems = memRes.value.data || [];
      setMembers(mems);
      if (!currentMemberId && mems.length > 0) {
        setCurrentMemberId(mems[0].id);
      }
    } else {
      anyFailed = true;
    }

    if (chRes.status === 'fulfilled' && chRes.value.success) {
      setChannels(chRes.value.data || []);
    } else {
      anyFailed = true;
    }

    if (catRes.status === 'fulfilled' && catRes.value.success) {
      setCategories(catRes.value.data || []);
    } else {
      anyFailed = true;
    }

    if (anyFailed) {
      // Soft warning — partial data still renders. Avoid toast.error so it
      // doesn't read like the whole dashboard is broken.
      toast('Some dashboard data could not be loaded. Showing what we have.', { icon: '⚠️' });
    }

    setLoading(false);
  }, [currentMemberId]);

  // Apply a single realtime seat event to local state in place. Uses the
  // functional setSeats updater so it never needs `seats` in a dependency
  // array — that keeps this callback (and therefore the WS effect that
  // closes over it) stable, so the socket isn't torn down and rebuilt on
  // every seat change. Payload shapes mirror seatManager.ts broadcasts.
  const applySeatEvent = useCallback((type: string, payload: Record<string, unknown>) => {
    const seatId = typeof payload.seat_id === 'string' ? payload.seat_id : null;
    if (!seatId) return;

    setSeats(prev => prev.map(seat => {
      if (seat.id !== seatId) return seat;

      if (type === 'seat_released') {
        // Seat freed (manual release or expiry) — reset to available.
        return {
          ...seat,
          status: 'available',
          claimed_by: null,
          claimed_at: null,
          expires_at: null,
          is_checkout: false,
          checkout_port: null,
          current_channel_name: null,
        };
      }

      if (type === 'seat_claimed') {
        return {
          ...seat,
          status: (payload.status as Seat['status']) ?? 'claimed',
          claimed_by: typeof payload.member_id === 'string' ? payload.member_id : seat.claimed_by,
          is_checkout: false,
          current_channel_name:
            typeof payload.current_channel_name === 'string'
              ? payload.current_channel_name
              : payload.current_channel_name === null
                ? null
                : seat.current_channel_name,
        };
      }

      if (type === 'seat_checkout') {
        return {
          ...seat,
          status: (payload.status as Seat['status']) ?? 'checkout',
          claimed_by: typeof payload.member_id === 'string' ? payload.member_id : seat.claimed_by,
          is_checkout: true,
          expires_at: typeof payload.expires_at === 'string' ? payload.expires_at : seat.expires_at,
        };
      }

      return seat;
    }));
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // WebSocket for real-time seat updates. The server requires a valid Supabase
  // access token on the URL — events are partitioned per user, so unauthenticated
  // sockets are rejected (close code 4401). URL pairs with the runtime API
  // base mode so a tailnet switch automatically routes WS through tailnet too.
  useEffect(() => {
    const baseWsUrl = getWsUrl();

    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout>;
    let cancelled = false;

    async function connect() {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token || cancelled) return;
      const url = `${baseWsUrl}${baseWsUrl.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
      ws = new WebSocket(url);

      // Token refresh: server pings ~every 50min asking for a fresh token.
      // bindWsTokenRefresh attaches the message handler that responds with
      // the current Supabase access_token. Without this, long-lived sockets
      // would be closed by the server at the refresh-grace timeout.
      bindWsTokenRefresh(ws);

      ws.onopen = () => setWsConnected(true);
      ws.onclose = () => {
        setWsConnected(false);
        if (!cancelled) reconnectTimer = setTimeout(connect, 5000);
      };
      ws.onerror = () => ws?.close();

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data) as { type?: string; data?: Record<string, unknown> };
          if (msg.type && ['seat_claimed', 'seat_released', 'seat_checkout'].includes(msg.type)) {
            // Micro-update local seat state from the event payload instead of
            // refetching all five endpoints. The server sends the affected
            // seat's id + new fields (see server/src/services/seatManager.ts
            // broadcast payloads), which is everything we need to patch the
            // single seat in place. Full refetch is reserved for initial load
            // and the explicit Refresh button.
            applySeatEvent(msg.type, msg.data ?? {});
          }
          // token_refresh_request / token_refresh_ack handled inside
          // bindWsTokenRefresh — no UI action needed here.
        } catch {}
      };
    }

    connect();

    return () => {
      cancelled = true;
      clearTimeout(reconnectTimer);
      ws?.close();
    };
    // applySeatEvent is stable (no deps) so the socket is opened once and
    // survives seat changes — we intentionally do NOT depend on `seats`.
  }, [applySeatEvent]);

  const handleClaimSeat = async (providerId: string) => {
    if (!currentMemberId) {
      toast.error('Select a family member first');
      return;
    }
    const res = await streamFlowApi.claimSeat({ provider_id: providerId, member_id: currentMemberId });
    if (res.success) {
      toast.success('Seat claimed!');
      // The WS seat_claimed event patches local state; refetch as a
      // fallback so the optimistic view is correct even if the socket is
      // momentarily down.
      fetchData();
    } else if (res.error?.code === 'SUBSCRIPTION_REQUIRED') {
      // 402 — not a failure to surface as "Failed". The household just needs
      // to renew. Route the web user toward billing; the dashboard's
      // BillingBanner (top of this page) carries the actual Subscribe /
      // Manage-billing CTA. '/account' falls through App's catch-all to '/'
      // so they land back here on the page that hosts the renew control.
      toast.error('Subscription needed — renew at gastv.gasweb.info/account');
      navigate('/account');
    } else {
      toast.error(res.error?.message || 'Failed to claim seat');
    }
  };

  const handleReleaseSeat = async (seatId: string) => {
    if (!currentMemberId) return;
    const res = await streamFlowApi.releaseSeat(seatId, currentMemberId);
    if (res.success) {
      toast.success('Seat released');
      fetchData();
    } else {
      toast.error(res.error?.message || 'Failed to release seat');
    }
  };

  // Swap the current member's primary provider — release any existing
  // seat, claim on the new provider. Single-seat invariant per
  // docs/SEAT_MODEL.md.
  const handleSwapPrimary = async (newProviderId: string) => {
    if (!currentMemberId) {
      toast.error('Select a family member first');
      return;
    }
    const res = await streamFlowApi.swapSeat({ member_id: currentMemberId, new_provider_id: newProviderId });
    if (res.success) {
      toast.success('Primary provider swapped');
      fetchData();
    } else {
      toast.error(res.error?.message || 'Failed to swap primary');
    }
  };

  const handlePlayChannel = (channel: Channel) => {
    navigate(`/watch/${channel.id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center text-gray-500">
          <Tv className="w-12 h-12 mx-auto mb-3 animate-pulse" />
          <p>Loading StreamFlow...</p>
        </div>
      </div>
    );
  }

  const totalSeats = seats.length;
  const availableSeats = seats.filter(s => s.status === 'available').length;
  const activeStreams = seats.filter(s => s.status !== 'available').length;

  // On TV form factors the dashboard pivots to the devices-first panel
  // — the user shouldn't be browsing channels in our SPA at all. They
  // install a native IPTV app (Tivimate / Smarters) and use the SPA
  // here just to mint credentials per device.
  if (isTvMode()) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6 tv:px-8 tv:py-10">
        <BillingBanner />
        <DevicesPanel />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 pb-20 md:pb-6">
      <InstallPrompt />
      <ConnectionBanner />
      <BillingBanner />
      {/* Header */}
      <div className="flex items-center justify-between mb-6 gap-2">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold truncate">StreamFlow</h1>
          <p className="hidden sm:block text-sm text-gray-500">Family IPTV Dashboard</p>
        </div>
        <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
          <div className="hidden sm:block"><HouseholdSwitcher /></div>
          <div className={`flex items-center gap-1 text-xs ${wsConnected ? 'text-green-600' : 'text-gray-400'}`}>
            <div className={`w-2 h-2 rounded-full ${wsConnected ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span className="hidden sm:inline">{wsConnected ? 'Live' : 'Offline'}</span>
          </div>
          <button onClick={fetchData} className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gray-100 rounded-lg" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          {/* Desktop-only nav buttons; mobile uses BottomNav */}
          <button onClick={() => navigate('/movies')} className="hidden md:flex p-2 min-w-[44px] min-h-[44px] items-center justify-center hover:bg-gray-100 rounded-lg" title="Movies">
            <Film className="w-4 h-4" />
          </button>
          <button onClick={() => navigate('/series')} className="hidden md:flex p-2 min-w-[44px] min-h-[44px] items-center justify-center hover:bg-gray-100 rounded-lg" title="Series">
            <Tv className="w-4 h-4" />
          </button>
          <button onClick={() => navigate('/library')} className="hidden md:flex p-2 min-w-[44px] min-h-[44px] items-center justify-center hover:bg-gray-100 rounded-lg" title="Jellyfin Library">
            <Sparkles className="w-4 h-4" />
          </button>
          <button onClick={() => navigate('/favorites')} className="hidden md:flex p-2 min-w-[44px] min-h-[44px] items-center justify-center hover:bg-gray-100 rounded-lg" title="Favorites">
            <Star className="w-4 h-4" />
          </button>
          <button onClick={() => navigate('/history')} className="hidden md:flex p-2 min-w-[44px] min-h-[44px] items-center justify-center hover:bg-gray-100 rounded-lg" title="Watch history">
            <Clock className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate('/onboarding')}
            className="hidden md:flex p-2 min-w-[44px] min-h-[44px] items-center justify-center hover:bg-gray-100 rounded-lg"
            title="Set up new device"
          >
            <Sparkles className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate('/settings/proxy-nodes')}
            className="hidden md:flex p-2 min-w-[44px] min-h-[44px] items-center justify-center hover:bg-gray-100 rounded-lg"
            title="Proxy nodes"
          >
            <Server className="w-4 h-4" />
          </button>
          <button
            onClick={() => navigate('/providers')}
            className="hidden md:flex p-2 min-w-[44px] min-h-[44px] items-center justify-center hover:bg-gray-100 rounded-lg"
            title="Manage Providers"
          >
            <Settings className="w-4 h-4" />
          </button>
          {/* Sign out — visible on every viewport. Desktop fits in the
              icon row; mobile users get the same affordance here AND in
              BottomNav's "More" sheet for muscle-memory consistency. */}
          <button
            onClick={handleSignOut}
            className="p-2 min-w-[44px] min-h-[44px] flex items-center justify-center hover:bg-gray-100 rounded-lg text-gray-600"
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500">Providers</p>
          <p className="text-2xl font-bold">{providers.length}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-gray-500">Total Seats</p>
          <p className="text-2xl font-bold">{totalSeats}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-green-600">Available</p>
          <p className="text-2xl font-bold text-green-600">{availableSeats}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-blue-600">Active Streams</p>
          <p className="text-2xl font-bold text-blue-600">{activeStreams}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-3 space-y-6">
          {/* No-members guidance — the seat-claim buttons require a selected
              family member, so with zero members the seat grid looks dead
              (no claim CTAs anywhere). This prominent banner sits ABOVE the
              grid and routes the user to create their first member. */}
          {members.length === 0 && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-4 rounded-lg border-2 border-blue-200 bg-blue-50 px-5 py-4">
              <div className="flex items-start gap-3 min-w-0 flex-1">
                <Users className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-blue-900">Add a family member to start watching</p>
                  <p className="text-xs text-blue-800/80 mt-0.5">
                    Seats are claimed per family member. Create one to claim a seat and start streaming.
                  </p>
                </div>
              </div>
              <button
                onClick={() => navigate('/members')}
                className="shrink-0 inline-flex items-center justify-center gap-2 rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700"
              >
                <Users className="w-4 h-4" />
                Add family member
              </button>
            </div>
          )}

          {/* Seat Overview */}
          {providers.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Radio className="w-5 h-5" />
                Seats
              </h2>
              {providers.map(provider => {
                const providerSeats = seats.filter(s => s.provider_id === provider.id);
                const hasAvailable = providerSeats.some(s => s.status === 'available');
                // True when the current member already holds a seat on a
                // DIFFERENT provider — they're a candidate for swapping their
                // primary to this one. Per docs/SEAT_MODEL.md.
                const memberHasSeatElsewhere = currentMemberId
                  ? seats.some(s => s.claimed_by === currentMemberId && s.provider_id !== provider.id && s.status !== 'available')
                  : false;
                const memberIsPrimaryHere = currentMemberId
                  ? seats.some(s => s.claimed_by === currentMemberId && s.provider_id === provider.id && s.status !== 'available')
                  : false;
                return (
                  <div key={provider.id} className="mb-4">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className="text-sm font-medium">{provider.name}</span>
                      <span className={`w-2 h-2 rounded-full ${
                        provider.health_status === 'healthy' ? 'bg-green-500' :
                        provider.health_status === 'unhealthy' ? 'bg-red-500' : 'bg-gray-300'
                      }`} />
                      {provider.is_shared && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                          🌐 Community
                        </span>
                      )}
                      {memberIsPrimaryHere && (
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-300"
                          title="Channels in your catalog are filtered to this provider plus multi-mirror channels — see docs/SEAT_MODEL.md"
                        >
                          Your primary
                        </span>
                      )}
                      {memberHasSeatElsewhere && hasAvailable && (
                        <button
                          onClick={() => handleSwapPrimary(provider.id)}
                          className="ml-auto inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100"
                          title="Release your current seat and claim one here. See docs/SEAT_MODEL.md."
                        >
                          Switch primary
                        </button>
                      )}
                      {hasAvailable && currentMemberId && (
                        <button
                          onClick={() => setCheckoutFor(provider)}
                          className={`${memberHasSeatElsewhere && hasAvailable ? '' : 'ml-auto'} inline-flex items-center gap-1 text-xs px-2 py-1 rounded-lg border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100`}
                          title="Reserve a seat exclusively for 24 hours"
                        >
                          <Lock className="w-3 h-3" />
                          Checkout 24h
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                      {providerSeats.map(seat => (
                        <SeatCard
                          key={seat.id}
                          seat={seat}
                          members={members}
                          currentMemberId={currentMemberId}
                          onClaim={() => handleClaimSeat(provider.id)}
                          onRelease={() => handleReleaseSeat(seat.id)}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Channel Guide */}
          <div>
            <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
              <Tv className="w-5 h-5" />
              Channels
            </h2>
            <ChannelGrid
              channels={channels}
              categories={categories}
              onPlay={handlePlayChannel}
              currentMemberId={currentMemberId}
            />
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <FamilySidebar
            members={members}
            seats={seats}
            currentMemberId={currentMemberId}
            onSelectMember={setCurrentMemberId}
            onAddMember={() => navigate('/members')}
          />
        </div>
      </div>

      {/* Empty state */}
      {providers.length === 0 && (
        <div className="text-center py-16 bg-white border rounded-lg">
          <Tv className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">Welcome to StreamFlow</h3>
          <p className="text-gray-500 mb-4 max-w-md mx-auto">
            Add your first IPTV provider to start streaming. Your family members can then claim seats and watch from anywhere.
          </p>
          <button
            onClick={() => navigate('/providers')}
            className="px-6 py-2 bg-slate-800 text-white rounded-lg hover:bg-slate-700"
          >
            Add Provider
          </button>
        </div>
      )}

      {checkoutFor && (
        <CheckoutModal
          provider={checkoutFor}
          memberId={currentMemberId}
          onClose={() => setCheckoutFor(null)}
          onCheckout={() => fetchData()}
        />
      )}
      <BottomNav />
    </div>
  );
}
